// covered-by: frontends/carrie/src/utils/__tests__/memoryTextMatch.test.ts

/**
 * How the sentence a saved memory supports is found again in the answer.
 *
 * warhol ships `supportText` as "the sentence exactly as shown in the reply,
 * markers stripped", and the page shows that sentence after markdown has been
 * rendered, citation markers have become badges and curly quotes have survived
 * or not. Three surfaces need to find it -- the numbering over the raw reply,
 * the rehype step over the rendered tree, and the paragraph fallback -- so the
 * folding lives here once.
 *
 * why an origin map and not a plain normalised string: the match is mapped back
 * onto the ORIGINAL text to place the highlight, and folding removes characters
 * (markers, emphasis marks) and collapses runs of whitespace, so an index in the
 * folded string is not an index in the original. Every folded character keeps
 * the original index it came from.
 *
 * why the same folding on both sides: a character the fold drops is dropped from
 * the needle and the haystack alike, so a legitimate `*` in a claim ("gut
 * health*") still matches. Nothing here needs to know which side had the markup.
 */

/** A citation marker in the prose: `[3]`, never a markdown link `[3](url)`. */
const MARKER = /\[\d{1,3}\](?!\()/g;

/**
 * A space that comes right before one of these is dropped.
 *
 * why: warhol removes a marker WITH the space before it (`( ?)\[n\]`), so
 * "says [3]." is sent as "says."; the page, where the marker is a badge with no
 * text, reads "says ." Dropping the space on both sides makes the two agree for
 * a marker, a badge and a plain space alike.
 */
const NO_SPACE_BEFORE = new Set([".", ",", ";", ":", "!", "?", ")"]);

/** Emphasis and code marks markdown renders away: present in a raw reply, absent from the page. */
const MARKUP = new Set(["*", "_", "`"]);

const QUOTES: Record<string, string> = {
  "‘": "'",
  "’": "'",
  "“": '"',
  "”": '"',
};

const DASHES = new Set(["–", "—", "−", "‐", "‑"]);

export interface Folded {
  /** Lower case, quotes and dashes unified, one space per whitespace run, no markers or markup. */
  text: string;
  /** For each folded UTF-16 unit, the index of the original unit it came from. */
  origin: number[];
}

function markerSpans(text: string): [number, number][] {
  return Array.from(text.matchAll(MARKER)).map((m) => [
    m.index ?? 0,
    (m.index ?? 0) + m[0].length,
  ]);
}

export function foldWithMap(text: string): Folded {
  const spans = markerSpans(text);
  let span = 0;
  let out = "";
  const origin: number[] = [];
  let i = 0;
  while (i < text.length) {
    while (span < spans.length && spans[span][1] <= i) span += 1;
    if (span < spans.length && i >= spans[span][0]) {
      i = spans[span][1];
      continue;
    }
    const codePoint = text.codePointAt(i) ?? 0;
    const ch = String.fromCodePoint(codePoint);
    const width = ch.length;
    let piece: string;
    if (MARKUP.has(ch)) {
      i += width;
      continue;
    } else if (/\s/.test(ch)) {
      if (out === "" || out.endsWith(" ")) {
        i += width;
        continue;
      }
      piece = " ";
    } else if (QUOTES[ch]) {
      piece = QUOTES[ch];
    } else if (DASHES.has(ch)) {
      piece = "-";
    } else {
      // why per code point and only when the width holds: lowercasing U+0130 yields two units, which would break the index map for every later char.
      const lower = ch.toLowerCase();
      piece = lower.length === ch.length ? lower : ch;
    }
    if (NO_SPACE_BEFORE.has(piece) && out.endsWith(" ")) {
      out = out.slice(0, -1);
      origin.pop();
    }
    out += piece;
    // why `i + k`: an astral character is two units, and mapping both to its first one cut a sentence that ends on it
    // between the halves, leaving a lone surrogate (bobai, PR #6260)
    for (let k = 0; k < piece.length; k += 1) {
      origin.push(i + Math.min(k, width - 1));
    }
    i += width;
  }
  // A trailing space has no original to point at that a range should include.
  if (out.endsWith(" ")) {
    out = out.slice(0, -1);
    origin.pop();
  }
  return { text: out, origin };
}

/** The comparable form of a sentence, with no origin map. */
export function foldForMatch(text: string): string {
  return foldWithMap(text).text;
}

/** A sentence shorter than this is too easy to find inside another one. */
export const MIN_SUPPORT_CHARS = 8;

/**
 * A line's markdown lead: bullet, number, heading or quote mark.
 *
 * why stripped from the needle: warhol's splitter keeps it ("- Activia ... line.")
 * and the page, where it is a list marker or a heading level, does not.
 */
const BLOCK_LEAD = /^\s*(?:(?:[-*+]|\d{1,3}[.)]|#{1,6}|>)\s+)+/;

/** `[text](url)` and `![alt](url)`: written in full in the sentence, shown as the text on the page. */
const LINK = /!?\[([^\]]*)\]\([^)]*\)/g;

/** A sentence without the markdown a reader never sees: its line lead and its link targets. */
export function plainSupportText(text: string): string {
  return text.replace(BLOCK_LEAD, "").replace(LINK, "$1");
}

/**
 * The folded forms a supported sentence can take on the page, most literal first.
 *
 * The raw reply holds the sentence as warhol sent it, link syntax and bullet
 * included; the rendered tree holds it with both gone. Each form is tried, so
 * one needle serves both, and a sentence that really starts "2. " keeps its
 * literal form first.
 */
export function supportNeedles(supportText: string): string[] {
  const noLead = supportText.replace(BLOCK_LEAD, "");
  const forms = [
    supportText,
    noLead,
    supportText.replace(LINK, "$1"),
    plainSupportText(supportText),
  ];
  const out: string[] = [];
  for (const form of forms) {
    const folded = foldForMatch(form);
    if (folded.length >= MIN_SUPPORT_CHARS && !out.includes(folded)) {
      out.push(folded);
    }
  }
  return out;
}

/**
 * Where `needle` sits in `haystack`, as a half-open range of ORIGINAL indices
 * into `haystack`; null when it is not there.
 */
export function findSentence(
  haystack: string,
  needle: string,
): { start: number; end: number } | null {
  const needles = supportNeedles(needle);
  if (needles.length === 0) return null;
  const hay = foldWithMap(haystack);
  for (const wanted of needles) {
    const at = hay.text.indexOf(wanted);
    if (at < 0) continue;
    return {
      start: hay.origin[at],
      end: hay.origin[at + wanted.length - 1] + 1,
    };
  }
  return null;
}

/** What separates words: whitespace and punctuation, in any of the product's scripts. */
const WORD_BREAK =
  /[\s.,;:!?()"'[\]{}/\\<>|=+&^~@#$\u2026\u060C\u061B\u061F-]+/;

/**
 * The distinct words of a text, folded.
 *
 * why a break set and not `\p{L}`: the unicode-property escapes need the `u` flag,
 * which this tsconfig target does not permit (TS1501).
 */
export function wordsOf(text: string): Set<string> {
  return new Set(foldForMatch(text).split(WORD_BREAK).filter(Boolean));
}

/** A block has to hold at least this share of a sentence's words to stand in for it. */
export const MIN_WORD_SHARE = 0.6;

/** Fewer distinct words than this and a "share" says nothing. */
export const MIN_WORDS_FOR_SHARE = 4;

/** What share of `needle`'s distinct words the `block` also holds; 0 when the needle is too short to judge. */
export function wordShare(needle: string, block: string): number {
  // why plain: a link target's url words are not in the rendered block.
  const wanted = wordsOf(plainSupportText(needle));
  if (wanted.size < MIN_WORDS_FOR_SHARE) return 0;
  const have = wordsOf(block);
  let shared = 0;
  wanted.forEach((word) => {
    if (have.has(word)) shared += 1;
  });
  return shared / wanted.size;
}
